import React, { useState, useMemo, useEffect } from 'react';

const TableVisualization = ({ data, onLoad, onError, isExpanded = false }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Extract data properties with defaults (safe for hooks)
  const { title, subtitle, columns = [], rows = [], options = {}, metadata } = data || {};
  const hasValidData = data && columns.length > 0 && rows.length > 0;

  // Responsive sizing for expanded mode
  const containerMaxHeight = isExpanded ? '600px' : '400px';
  const fontSize = isExpanded ? '14px' : '13px';

  // Notify when table is loaded
  useEffect(() => {
    if (onLoad && hasValidData) {
      onLoad();
    }
  }, [onLoad, hasValidData]);

  // Performance optimization for filtered and sorted data
  const filteredRows = useMemo(() => {
    if (!hasValidData) return [];
    
    return rows.filter(row =>
      searchTerm === '' ||
      columns.some(column =>
        String(row[column.key] || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [rows, columns, searchTerm, hasValidData]);

  const sortedRows = useMemo(() => {
    if (!hasValidData || !sortColumn) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      
      if (aValue === bValue) return 0;
      
      const comparison = aValue < bValue ? -1 : 1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredRows, sortColumn, sortDirection, hasValidData]);

  // VALIDATION CHECKS AFTER ALL HOOKS
  if (!data) {
    if (onError) {
      onError('No table data provided');
    }
    return (
      <div className="table-no-data">
        <div className="no-data-icon">📋</div>
        <p>No table data available</p>
        <small>Unable to generate table</small>
      </div>
    );
  }

  if (!hasValidData) {
    if (onError) {
      onError('Invalid table structure - missing columns or rows');
    }
    return (
      <div className="table-error">
        <div className="error-icon">⚠️</div>
        <p>Table data is incomplete</p>
        <small>Missing {columns.length === 0 ? 'columns' : 'rows'}</small>
      </div>
    );
  }

  // Format cell values based on column type
  const formatCellValue = (value, column) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    switch (column.type) {
      case 'currency':
        if (typeof value === 'number') {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(value);
        }
        return value;
      
      case 'percentage':
        if (typeof value === 'number') {
          return `${(value * 100).toFixed(2)}%`;
        }
        return value;
      
      case 'number':
        if (typeof value === 'number') {
          return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          }).format(value);
        }
        return value;
      
      case 'date':
        if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) {
          return new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        }
        return value;
      
      default:
        return value;
    }
  };

  // Get cell color based on column configuration and value
  const getCellColor = (value, column) => {
    if (column.color && typeof column.color === 'function') {
      return column.color(value);
    }
    
    if (options.colorCoding && column.key === options.colorCoding.column) {
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      if (!isNaN(numValue)) {
        if (numValue > 0) return options.colorCoding.positive || '#10b981';
        if (numValue < 0) return options.colorCoding.negative || '#ef4444';
        return options.colorCoding.neutral || '#6b7280';
      }
    }
    
    return null;
  };


  const handleSort = (columnKey) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column.sortable) return;
    
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div className={`table-container ${isExpanded ? 'expanded' : ''}`} style={{ maxHeight: containerMaxHeight }}>
      {title && <h3 style={{ fontSize: isExpanded ? '18px' : '16px' }}>{title}</h3>}
      {subtitle && (
        <div style={{
          padding: '0 20px 12px',
          fontSize: fontSize,
          color: '#6b7280',
          fontWeight: '500'
        }}>
          {subtitle}
        </div>
      )}
      
      {/* Search/Filter */}
      {options.filterable && (
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          background: 'rgba(249, 250, 251, 0.5)'
        }}>
          <input
            type="text"
            placeholder="Search table..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid rgba(209, 213, 219, 0.8)',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#10b981'}
            onBlur={(e) => e.target.style.borderColor = 'rgba(209, 213, 219, 0.8)'}
          />
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{
                    textAlign: column.align || 'left',
                    cursor: column.sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    position: 'relative'
                  }}
                  onClick={() => column.sortable && handleSort(column.key)}
                  onMouseEnter={(e) => {
                    if (column.sortable) {
                      e.target.style.background = 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (column.sortable) {
                      e.target.style.background = 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)';
                    }
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: column.align === 'center' ? 'center' : 
                                   column.align === 'right' ? 'flex-end' : 'flex-start',
                    gap: '6px'
                  }}>
                    <span>{column.header}</span>
                    {column.sortable && (
                      <span style={{
                        fontSize: '10px',
                        opacity: sortColumn === column.key ? 1 : 0.5,
                        transition: 'opacity 0.2s'
                      }}>
                        {getSortIcon(column.key)}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#9ca3af',
                    fontStyle: 'italic'
                  }}
                >
                  {searchTerm ? 'No matching results found' : 'No data available'}
                </td>
              </tr>
            ) : (
              sortedRows.map((row, index) => (
                <tr key={index}>
                  {columns.map((column) => {
                    const cellValue = row[column.key];
                    const formattedValue = formatCellValue(cellValue, column);
                    const cellColor = getCellColor(cellValue, column);
                    
                    return (
                      <td
                        key={column.key}
                        style={{
                          textAlign: column.align || 'left',
                          color: cellColor || '#4b5563',
                          fontWeight: cellColor ? '600' : '400'
                        }}
                      >
                        {formattedValue}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Metadata footer */}
      {metadata && (
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid rgba(226, 232, 240, 0.8)',
          background: 'rgba(249, 250, 251, 0.5)',
          fontSize: '11px',
          color: '#6b7280',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>
            {metadata.totalRows && `${sortedRows.length}${sortedRows.length !== metadata.totalRows ? ` of ${metadata.totalRows}` : ''} rows`}
            {searchTerm && ` (filtered)`}
          </span>
          {metadata.lastUpdated && (
            <span>
              Updated: {new Date(metadata.lastUpdated).toLocaleTimeString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default TableVisualization;